/**
 * Resolves the currently selected location from Redux + menu data.
 * Returns the MenuLocation if selected (or auto-selected for single-location menus),
 * plus a pre-built address string.
 */
import { useMemo } from 'react'
import { useAppSelector } from '@/store/hooks'
import { useMenuData } from '@/hooks/useMenuData'
import type { MenuLocation } from '@/hooks/useMenuData'
import { restaurantConfig } from '@/config/restaurant'

interface SelectedLocationResult {
  location: MenuLocation | null
  /** Full address string for the selected location, or restaurant/config fallback */
  address: string
}

export function useSelectedLocation(): SelectedLocationResult {
  const selectedId = useAppSelector((s) => s.order.selectedLocationId)
  const orderType = useAppSelector((s) => s.order.orderType)
  const { menus, restaurant } = useMenuData()

  const menuLocations = useMemo(() => {
    const channel = orderType === 'delivery' ? 'delivery' : 'pickup'
    const matched = menus.find((m) => m.channel === channel)
    return matched?.locations ?? menus[0]?.locations ?? []
  }, [menus, orderType])

  const location = useMemo<MenuLocation | null>(() => {
    if (menuLocations.length === 0) return null
    if (menuLocations.length === 1) return menuLocations[0]
    return menuLocations.find((l) => l.id === selectedId) ?? null
  }, [menuLocations, selectedId])

  const address = useMemo(() => {
    if (location) {
      return [location.address, location.city, location.state, location.postal_code]
        .filter(Boolean)
        .join(', ')
    }
    if (restaurant?.address) {
      return [restaurant.address, restaurant.city, restaurant.state, restaurant.postal_code]
        .filter(Boolean)
        .join(', ')
    }
    return `${restaurantConfig.address.street}, ${restaurantConfig.address.city}, ${restaurantConfig.address.state} ${restaurantConfig.address.zipCode}`
  }, [location, restaurant])

  return { location, address }
}
